import { useEffect, useMemo, useRef, useState } from "react";
import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  SupportRequest,
  SupportRequestReason,
  SupportRequestStatusEnum,
} from "@/app/entities/SupportRequest";
import { useAuth } from "@/app/hooks/useAuth";
import {
  SUPPORT_REQUEST_COUNTS_QUERY_KEY,
  useSupportRequestCounts,
} from "@/app/hooks/useSupportRequestCounts";
import { supportRequestService } from "@/app/services/supportRequestService";
import {
  ListMySupportRequestsParams,
  ListMySupportRequestsResponse,
} from "@/app/services/supportRequestService/listAllSupport";
import { useToast } from "@/view/components/ui/use-toast";

export const SUPPORT_LIST_QUERY_KEY = "supportRequestList";

const PAGE_SIZE = 10;
const STALE_TIME = 30_000;

export type SupportListTab = "mine" | "inProgress" | "all" | "team" | "stats";

export const STATS_TAB: SupportListTab = "stats";

export const SUPPORT_LIST_TABS: SupportListTab[] = [
  "mine",
  "inProgress",
  "all",
];

export const SUPPORT_LIST_TABS_WITH_TEAM: SupportListTab[] = [
  "mine",
  "team",
  "inProgress",
  "all",
];

// gerente, supervisor e líder sem papel de suporte só acompanham a equipe
export const TEAM_LEAD_TABS: SupportListTab[] = ["team"];

export const SUPPORT_LIST_TAB_LABEL: Record<SupportListTab, string> = {
  mine: "Meus",
  team: "Minha equipe",
  inProgress: "Em atendimento",
  all: "Todos",
  stats: "Estatísticas",
};

export const SUPPORT_LIST_TAB_TITLE: Record<SupportListTab, string> = {
  mine: "Meus chamados",
  inProgress: "Em atendimento",
  all: "Todos os chamados",
  team: "Chamados da minha equipe",
  stats: "Estatísticas de chamados",
};

// assumir o próximo atribui o chamado a quem clicou: ele aparece em "Meus"
export const CLAIM_NEXT_TAB: SupportListTab = "mine";

const EMPTY_TAB_MESSAGE: Record<SupportListTab, string> = {
  mine: "Você ainda não assumiu nenhum chamado",
  inProgress: "Nenhum chamado em atendimento",
  all: "Nenhum chamado por aqui",
  team: "Nenhum chamado aberto pela sua equipe no momento",
  stats: "",
};

export const ALL_ASSIGNEES = "all";

// o time não atende os direcionados ao WhatsApp: eles ficam fora das três
// listas (e dos totais) — a mesma regra do "Atender próximo", que os ignora
const HIDDEN_REASONS = [SupportRequestReason.WHATSAPP_DIRECT];

// "Em atendimento" é por status, não por ter responsável: um chamado movido
// pela tela de detalhe fica IN_PROGRESS sem ninguém atribuído e precisa
// aparecer aqui do mesmo jeito. É o que faz Todos = abertos + em atendimento.
const IN_PROGRESS_STATUSES = [
  SupportRequestStatusEnum.IN_PROGRESS,
  SupportRequestStatusEnum.ESCALATED_TECHNICAL,
  SupportRequestStatusEnum.AWAITING_SELLER,
];

/**
 * Meus: os seus · Em atendimento: os que já saíram do OPEN · Todos: tudo que o
 * time atende, com os sem responsável no topo. Meus e Todos mostram também
 * resolvidos, fechados e cancelados; os contadores vêm do `stats/counts` e
 * ficam só nos pendentes.
 */
export const supportListParams = (
  tab: SupportListTab,
  userId?: string,
  assignee?: string,
): ListMySupportRequestsParams => {
  const base: ListMySupportRequestsParams = { excludeReason: HIDDEN_REASONS };

  if (tab === "mine") return { ...base, assignedTo: userId, sort: "pending" };

  if (tab === "inProgress")
    return {
      ...base,
      status: IN_PROGRESS_STATUSES,
      assignedTo: assignee,
      sort: "pending",
    };

  if (tab === "team")
    return {
      ...base,
      team: true,
      status: [SupportRequestStatusEnum.OPEN, ...IN_PROGRESS_STATUSES],
      sort: "pending",
    };

  return { ...base, sort: "unassigned" };
};

export const supportListQueryOptions = (
  tab: SupportListTab,
  userId?: string,
  assignee?: string,
) => ({
  queryKey: [SUPPORT_LIST_QUERY_KEY, tab, assignee ?? ALL_ASSIGNEES],
  queryFn: ({ pageParam }: { pageParam: number }) =>
    supportRequestService.listSupportRequests({
      ...supportListParams(tab, userId, assignee),
      skip: pageParam,
      limit: PAGE_SIZE,
    }),
  initialPageParam: 0,
  getNextPageParam: (lastPage: ListMySupportRequestsResponse) => {
    const nextSkip = lastPage.pagination.skip + lastPage.pagination.limit;
    return nextSkip >= lastPage.pagination.total ? undefined : nextSkip;
  },
  enabled: tab !== "mine" || !!userId,
  staleTime: STALE_TIME,
});

interface UseSupportQueueParams {
  tab: SupportListTab;
  selectedId?: string;
  onSelect?: (request: SupportRequest | null) => void;
}

export function useSupportQueue({
  tab,
  selectedId,
  onSelect,
}: UseSupportQueueParams) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const userId = user?._id;

  const [assignee, setAssignee] = useState(ALL_ASSIGNEES);

  const hasAssigneeFilter = tab === "inProgress";

  const { data: assignees = [] } = useQuery({
    queryKey: ["supportTeam"],
    queryFn: supportRequestService.getSupportTeam,
    enabled: hasAssigneeFilter,
    staleTime: 5 * 60_000,
  });

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery(
      supportListQueryOptions(
        tab,
        userId,
        hasAssigneeFilter && assignee !== ALL_ASSIGNEES ? assignee : undefined,
      ),
    );

  const queue = useMemo(
    () => data?.pages.flatMap((page) => page.supportRequests) ?? [],
    [data],
  );

  // contadores e o estado do botão saem do stats, que é uma requisição só:
  // nenhuma lista é carregada pra descobrir número
  const counts = useSupportRequestCounts();

  const pendingCount =
    tab === STATS_TAB ? 0 : counts[tab as Exclude<SupportListTab, "stats">];
  const hasUnassigned = counts.claimable > 0;

  // o sentinel vive dentro da coluna, que é o container que rola
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) fetchNextPage();
      },
      { root: scrollRef.current, rootMargin: "200px", threshold: 0.1 },
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, queue.length]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [tab, assignee]);

  const { mutate: claimNext, isPending: isClaiming } = useMutation({
    mutationFn: () => supportRequestService.claimNextSupportRequest(),
    onSuccess: (claimed) => {
      queryClient.invalidateQueries({ queryKey: ["mySupportRequests"] });
      queryClient.invalidateQueries({
        queryKey: [SUPPORT_REQUEST_COUNTS_QUERY_KEY],
      });

      if (!claimed) {
        queryClient.invalidateQueries({ queryKey: [SUPPORT_LIST_QUERY_KEY] });
        toast({ title: "Não há chamados em aberto no momento" });
        return;
      }

      queryClient.invalidateQueries({
        queryKey: [SUPPORT_LIST_QUERY_KEY, "inProgress"],
      });
      queryClient.invalidateQueries({
        queryKey: [SUPPORT_LIST_QUERY_KEY, "all"],
      });

      queryClient.setQueryData<InfiniteData<ListMySupportRequestsResponse>>(
        [SUPPORT_LIST_QUERY_KEY, CLAIM_NEXT_TAB, ALL_ASSIGNEES],
        (current) => {
          if (!current?.pages.length) return current;

          const isListed = current.pages.some((page) =>
            page.supportRequests.some((request) => request._id === claimed._id),
          );

          if (isListed) {
            return {
              ...current,
              pages: current.pages.map((page) => ({
                ...page,
                supportRequests: page.supportRequests.map((request) =>
                  request._id === claimed._id ? claimed : request,
                ),
              })),
            };
          }

          const [firstPage, ...rest] = current.pages;

          return {
            ...current,
            pages: [
              {
                ...firstPage,
                pagination: {
                  ...firstPage.pagination,
                  total: firstPage.pagination.total + 1,
                },
                supportRequests: [claimed, ...firstPage.supportRequests],
              },
              ...rest,
            ],
          };
        },
      );

      onSelect?.(claimed);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title:
          error.message ||
          "Erro ao assumir chamado, tente novamente mais tarde!",
      });
    },
  });

  const handleSelect = (request: SupportRequest) => {
    onSelect?.(request._id === selectedId ? null : request);
  };

  return {
    queue,
    pendingCount,
    title: SUPPORT_LIST_TAB_TITLE[tab],
    emptyMessage: EMPTY_TAB_MESSAGE[tab],
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    hasUnassigned,
    hasAssigneeFilter,
    assignees,
    assignee,
    setAssignee,
    scrollRef,
    sentinelRef,
    claimNext,
    isClaiming,
    handleSelect,
  };
}
